/**
 * Platform Aware Payment Button
 * 
 * Smart wrapper that shows the appropriate payment button based on platform:
 * - iOS Native: Apple In-App Purchase via NativePaymentButton
 * - Android Native: Google Play Billing via NativePaymentButton
 * - Web: PayPal via existing PayPalButton
 */

import React from 'react';
import { usePaymentPlatform } from '../contexts/PaymentPlatformContext';
import { NativePaymentButton } from './NativePaymentButton';
import { PayPalButton } from './PayPalButton';
import { Alert, AlertDescription } from './ui/alert';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PlatformAwarePaymentButtonProps {
  tier: 'standard' | 'premium' | 'ultimate';
  billingCycle: 'monthly' | 'annual';
  label?: string;
  variant?: 'default' | 'outline' | 'secondary';
  className?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export const PlatformAwarePaymentButton: React.FC<PlatformAwarePaymentButtonProps> = ({
  tier,
  billingCycle,
  label,
  variant = 'default',
  className = '',
  onSuccess,
  onError,
}) => {
  const { paymentMethod, isNative, isLoading, platform } = usePaymentPlatform();
  const { t } = useTranslation();

  // Show loading state while initializing
  if (isLoading) {
    return (
      <div className="w-full h-10 bg-muted animate-pulse rounded-md" />
    );
  }

  // For native platforms, show the native payment button
  if (isNative) {
    return (
      <div className="space-y-3">
        <NativePaymentButton
          tier={tier}
          billingCycle={billingCycle}
          label={label}
          variant={variant}
          className={className}
          onSuccess={onSuccess}
          onError={onError}
        />
        
        {/* Platform-specific note */}
        <Alert variant="default" className="bg-muted/50">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {paymentMethod === 'apple' 
              ? t('subscription.managedByApple', { 
                  defaultValue: 'Subscription managed by Apple. Manage in iOS Settings > Subscriptions.' 
                })
              : t('subscription.managedByGoogle', { 
                  defaultValue: 'Subscription managed by Google Play. Manage in Google Play Store > Subscriptions.' 
                })
            }
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // For web platform, show PayPal button
  return (
    <PayPalButton 
      tier={tier} 
      billingCycle={billingCycle}
      label={label || t('subscription.subscribeWithPayPal', { defaultValue: 'Subscribe with PayPal' })}
      variant={variant}
    />
  );
};
